import { useCallback, useEffect, useRef, useState } from "react";
import { estimateF0Yin, rmsDbfs } from "@shared/prosodyCore.js";

/**
 * Nagrywanie jednego zdania z zywym podgladem.
 *
 * Podglad liczony jest LOKALNIE, tym samym kodem co pomiar na serwerze
 * (shared/prosodyCore.ts). Dzieki temu pierscien glosnosci i linia tonu
 * pojawiaja sie natychmiast, bez czekania na siec, a liczby na ekranie nie
 * rozjezdzaja sie z liczbami w wyniku.
 *
 * Uzywamy AnalyserNode zamiast AudioWorkletu. Worklet dziala w osobnym watku
 * i nie blokuje renderowania, ale wymaga osobnego pliku poza bundlem, wiec nie
 * dalo by sie w nim uzyc wspolnego kodu bez duplikacji. Przy 30 klatkach na
 * sekunde i liczeniu tonu co trzecia klatke koszt jest pomijalny, a jedno
 * zrodlo prawdy warte wiecej niz teoretyczna plynnosc.
 */

/** Co ktora klatka liczymy ton. RMS jest tani, YIN nie. */
const F0_EVERY_N_FRAMES = 3;

/** Zakres szukania tonu w podgladzie. Wezszy niz na serwerze - liczy sie szybkosc. */
const LIVE_F0_MIN = 70;
const LIVE_F0_MAX = 400;

export interface LiveSample {
  /** Poziom znormalizowany do 0-1, gotowy do narysowania. */
  level: number;
  f0Hz: number | null;
}

export type RecorderState =
  | "idle"
  | "requesting"
  | "recording"
  | "processing"
  | "unsupported"
  | "denied"
  /** Urzadzenie odpadlo w trakcie nagrywania - inny stan niz odmowa dostepu. */
  | "przerwane";

export interface RecordedShot {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

/** Format nagrania. Chrome i Firefox daja webm, Safari tylko mp4. */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

/** Mapuje dBFS na 0-1. Ponizej -60 dB to dla oka i tak cisza. */
function levelFromDb(db: number): number {
  return Math.max(0, Math.min(1, (db + 60) / 60));
}

export function useMicRecorder(maxSeconds: number) {
  const [state, setState] = useState<RecorderState>("idle");
  const [live, setLive] = useState<LiveSample>({ level: 0, f0Hz: null });
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pitchTrack, setPitchTrack] = useState<Array<number | null>>([]);

  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const chunksRef = useRef<Blob[]>([]);
  const resolveRef = useRef<((shot: RecordedShot | null) => void) | null>(null);

  useEffect(() => {
    if (pickMimeType() === null) setState("unsupported");
  }, []);

  const teardown = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    recorderRef.current = null;
  }, []);

  useEffect(() => teardown, [teardown]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async (): Promise<RecordedShot | null> => {
    const mimeType = pickMimeType();
    if (!mimeType) {
      setState("unsupported");
      return null;
    }

    setState("requesting");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Wylaczamy obrobke przegladarki. Automatyczna regulacja wzmocnienia
          // wyrownalaby glosnosc, czyli skasowala dokladnie to, co mierzymy.
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
    } catch {
      setState("denied");
      return null;
    }

    streamRef.current = stream;
    const context = new AudioContext();
    contextRef.current = context;

    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    const buffer = new Float32Array(analyser.fftSize);
    let frameIndex = 0;
    let lastF0: number | null = null;
    const track: Array<number | null> = [];

    const tick = () => {
      analyser.getFloatTimeDomainData(buffer);
      const db = rmsDbfs(buffer);

      if (frameIndex % F0_EVERY_N_FRAMES === 0) {
        const estimate = estimateF0Yin(buffer, context.sampleRate, {
          minHz: LIVE_F0_MIN,
          maxHz: LIVE_F0_MAX,
        });
        lastF0 = estimate?.hz ?? null;
        track.push(lastF0);
        setPitchTrack([...track]);
      }
      frameIndex++;

      setLive({ level: levelFromDb(db), f0Hz: lastF0 });
      setElapsedMs(Date.now() - startedAtRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    const recorder = new MediaRecorder(stream, { mimeType });
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAtRef.current;
      teardown();
      setLive({ level: 0, f0Hz: null });
      setState("processing");
      resolveRef.current?.({
        blob: new Blob(chunksRef.current, { type: mimeType }),
        mimeType,
        durationMs,
      });
      resolveRef.current = null;
    };
    /**
     * Bez tej obslugi wyrwany mikrofon wieszal caly widok.
     *
     * `start()` czeka na obietnice rozwiazywana WYLACZNIE w onstop, a gdy
     * urzadzenie znika, MediaRecorder emituje `error` i do onstop nigdy nie
     * dochodzi. Obietnica nie wracala, `finally { reset() }` w MicPage sie nie
     * wykonywalo i przycisk zostawal w stanie nagrywania az do przeladowania
     * strony - na pokazie na zywo to koniec demonstracji.
     */
    recorder.onerror = () => {
      teardown();
      setLive({ level: 0, f0Hz: null });
      setState("przerwane");
      resolveRef.current?.(null);
      resolveRef.current = null;
    };

    startedAtRef.current = Date.now();
    setPitchTrack([]);
    setElapsedMs(0);
    setState("recording");
    recorder.start();
    rafRef.current = requestAnimationFrame(tick);

    // Twardy limit. Bez niego pierwsza osoba, ktora zapomni puscic przycisk,
    // wysle na serwer kilkuminutowy plik.
    const timeout = setTimeout(stop, maxSeconds * 1000);

    return new Promise<RecordedShot | null>((resolve) => {
      resolveRef.current = (shot) => {
        clearTimeout(timeout);
        resolve(shot);
      };
    });
  }, [maxSeconds, stop, teardown]);

  const reset = useCallback(() => setState("idle"), []);

  return { state, live, elapsedMs, pitchTrack, start, stop, reset };
}
